import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "bg-white rounded-xl p-6 mx-3 flex flex-col" }
const _hoisted_2 = { class: "flex w-full items-center justify-center mt-4" }
const _hoisted_3 = { class: "flex items-center justify-center w-[40px] h-[40px] rounded-full bg-anykrowd-gray/30" }
const _hoisted_4 = { class: "flex w-full items-center justify-center mb-4" }
const _hoisted_5 = { class: "flex items-center justify-center w-[60px] h-[60px] rounded-full bg-anykrowd-gray/30 mb-4" }
const _hoisted_6 = { class: "font-sourcesanspro-semibold text-lg" }
const _hoisted_7 = { class: "text-anykrowd-dark" }
const _hoisted_8 = { class: "flex flex-col justify-between" }
const _hoisted_9 = { class: "flex flex-col h-full justify-center px-[34px]" }
const _hoisted_10 = { class: "icon flex justify-center pt-20" }
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "pb-4 pt-6 mb-2 rounded-md" }
const _hoisted_13 = { class: "font-sourcesanspro-bold text-white text-md text-center" }
const _hoisted_14 = { class: "max-w-xs mx-auto mb-6 text-white text-sm text-center" }
const _hoisted_15 = { class: "flex flex-col justify-center" }
const _hoisted_16 = {
  key: 0,
  class: "separator mb-6"
}
const _hoisted_17 = { class: "font-sourcesanspro-regular text-base text-white" }
const _hoisted_18 = { class: "mb-4 rounded-md" }
const _hoisted_19 = {
  key: 0,
  class: "flex justify-center mt-2 mb-2"
}
const _hoisted_20 = { class: "px-4 pb-2 rounded-md" }

import { onBeforeMount, ref, watch } from 'vue';
import { Capacitor } from '@capacitor/core';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonContent, IonIcon } from '@ionic/vue';
import { mailOutline, closeOutline, callOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth/authStore';
import useAuth from '@/modules/auth/composables/useAuth';
import CustomButton from '@/shared/components/CustomButton.vue';
import ModalThree from '@/shared/components/ui/ModalThree.vue';
import SignInSignUpAlert from '@/shared/components/SignInSignUpAlert.vue';
import SocialLoginButtons from '@/modules/auth/components/SocialLoginButtons.vue';
import TopBar from '@/shared/components/TopBar.vue';
import BackButton from '@/shared/components/BackButton.vue';
import useTenant from '@/shared/composables/useTenant';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import ShareAppButton from '@/shared/components/share/ShareAppButton.vue';
import InstallApp from '@/shared/components/install/InstallApp.vue';
import { useCacheImage } from '@/shared/composables/useCacheImage';
import useAuthHelpers from '@/modules/auth/composables/useAuthHelpers';
import { SocialProvider } from '@/shared/interfaces/user';
import PreviousLoginModal from '@/modules/auth/components/PreviousLoginModal.vue';

// Init

export default /*@__PURE__*/_defineComponent({
  __name: 'SignUpHomePage',
  setup(__props) {

const history = window.history;
const assetStore = useAssetsStore();
const authStore = useAuthStore();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig, anySocialLoginEnabled, tenantStyles } = getTenantConfigAndStyles();
const { setRedirect } = useAuth();
const route = useRoute();
const { t } = useI18n();
const { loadImage } = useCacheImage();
const logoUrl = ref();
const { detectPreviousLoginMethod } = useAuthHelpers();

const previousLogin = ref<{ provider: string; timestamp: string } | null>(null);
const showPreviousLoginModal = ref(false);

const isSocialProvider = (provider: string) => {
	return ['google', 'facebook', 'apple'].includes(provider);
};

const handleLoginWithPrevious = async () => {
	if (!previousLogin.value) return;

	const { provider } = previousLogin.value;

	if (isSocialProvider(provider)) {
		const { oAuthLogin } = useAuth();
		if (tenantStyles.value) {
			await oAuthLogin(provider as SocialProvider, tenantStyles.value);
			await router.push('/dashboard');
		}
	} else {
		console.warn('Previous login method is not a social provider, redirecting to sign-in home.');
		await router.push('/auth/signinhome?previousLogin=true');
	}
};

const handleUseOtherMethod = () => {
	showPreviousLoginModal.value = false;
};

onBeforeMount(async () => {
	await authStore.authenticateWithSavedToken();
	if (authStore.isAuthenticated()) {
		await router.push('dashboard');
	} else {
		const detected = await detectPreviousLoginMethod();
		if (detected) {
			previousLogin.value = detected;
			showPreviousLoginModal.value = true;
		}
	}
});

watch(
	assetStore,
	async (assetStore) => {
		if (assetStore.logoUrl) {
			let logoImage = assetStore.logoUrl.split('/').pop();
			logoImage = logoImage ?? 'logo';
			logoUrl.value = await loadImage('logo', logoImage, assetStore.logoUrl);
		}
	},
	{ immediate: true },
);

// Save redirection to use it when the register / login process ends
if (route.query.callbackPage && route.query.id) {
	setRedirect({
		callbackPage: route.query.callbackPage.toString(),
		id: route.query.id.toString(),
	});
}

// Modals
const modalState = ref(false);
const modalType = ref('');
const setModal = (state: boolean, type?: string) => {
	modalState.value = state;
	modalType.value = type === 'tos' ? 'tos' : 'pp';
};

// onLoginSuccess
const onLoginSuccess = () => {
	router.push('dashboard');
};

// goTo
const goTo = async (link: string) => {
	await router.push(link);
};

// Go back
const goBack = () => {
	router.back();
};

return (_ctx: any,_cache: any) => {
  const _component_ion_modal = _resolveComponent("ion-modal")!
  const _directive_dompurify_html = _resolveDirective("dompurify-html")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_ion_modal, {
      "is-open": showPreviousLoginModal.value,
      "backdrop-dismiss": true,
      class: "modal-wrapper"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(PreviousLoginModal, {
            "previous-login": previousLogin.value,
            onContinueWithPrevious: handleLoginWithPrevious,
            onUseOther: handleUseOtherMethod,
            onOnLoginSuccess: onLoginSuccess
          }, null, 8, ["previous-login"]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_unref(IonIcon), {
                class: "text-[24px] text-black cursor-pointer",
                icon: _unref(closeOutline),
                onClick: handleUseOtherMethod
              }, null, 8, ["icon"])
            ])
          ])
        ])
      ]),
      _: 1
    }, 8, ["is-open"]),
    _createVNode(_unref(IonContent), null, {
      default: _withCtx(() => [
        _createVNode(ModalThree, { "is-open": modalState.value }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(IonIcon), {
                  class: "text-[38px] text-black cursor-pointer",
                  icon: _unref(closeOutline),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (setModal(false)))
                }, null, 8, ["icon"])
              ])
            ]),
            _createElementVNode("h1", _hoisted_6, _toDisplayString(modalType.value === 'tos' ? _unref(t)('form.terms_and_conditions') : _unref(t)('form.privacy_policy')), 1),
            _withDirectives(_createElementVNode("div", _hoisted_7, null, 512), [
              [_directive_dompurify_html, modalType.value === 'tos' ? _unref(tenantConfig)!.terms_and_conditions : _unref(tenantConfig)!.privacy_policy]
            ])
          ]),
          _: 1
        }, 8, ["is-open"]),
        _createElementVNode("div", _hoisted_8, [
          (_unref(tenantConfig) && _unref(tenantConfig).enable_public_mode)
            ? (_openBlock(), _createBlock(TopBar, {
                key: 0,
                class: "flex px-[34px]"
              }, {
                left: _withCtx(() => [
                  _createVNode(BackButton, {
                    onClick: () => _unref(history).state.back ? goBack() : goTo('dashboard')
                  }, null, 8, ["onClick"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("img", {
                src: logoUrl.value,
                class: "max-w-[180px]"
              }, null, 8, _hoisted_11)
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("h1", _hoisted_13, _toDisplayString(_unref(tenantConfig)?.welcome_title), 1),
              _createElementVNode("div", _hoisted_14, _toDisplayString(_unref(tenantConfig)?.welcome_line), 1),
              _createElementVNode("div", _hoisted_15, [
                _createVNode(CustomButton, {
                  onOnClick: _cache[1] || (_cache[1] = ($event: any) => (goTo('/auth/signupwithemail'))),
                  text: "auth.sign_up_home_page.signUpEmail",
                  iconLeft: _unref(mailOutline)
                }, null, 8, ["iconLeft"])
              ])
            ]),
            (_unref(anySocialLoginEnabled)() || _unref(tenantConfig)?.enable_phone_login)
              ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                  _createElementVNode("h5", null, [
                    _createElementVNode("span", _hoisted_17, _toDisplayString(_ctx.$t(`auth.sign_up_home_page.orSignUpWith`)), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_18, [
              (_unref(tenantConfig)?.enable_phone_login)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                    _createVNode(CustomButton, {
                      class: "ion-no-margin",
                      fill: "outline",
                      onOnClick: _cache[2] || (_cache[2] = ($event: any) => (goTo('/auth/signupwithphone'))),
                      text: "auth.sign_up_home_page.signUpPhone",
                      iconLeft: _unref(callOutline)
                    }, null, 8, ["iconLeft"])
                  ]))
                : _createCommentVNode("", true),
              _createVNode(SocialLoginButtons, { onOnLoginSuccess: onLoginSuccess })
            ]),
            _createElementVNode("div", _hoisted_20, [
              _createVNode(SignInSignUpAlert, {
                buttonText: "components.sign_in.buttonText",
                text: "components.sign_in.text",
                link: "/auth/signinhome"
              })
            ]),
            _createVNode(ShareAppButton),
            _createVNode(InstallApp)
          ])
        ])
      ]),
      _: 1
    })
  ], 64))
}
}

})